import CloseOutlined from '@ant-design/icons/CloseOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import {
  Button,
  Form,
  Input,
  message,
  Space,
  Steps,
  Tag,
  Tooltip,
  Upload,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { blue, redPrimary } from '../../../../color'
import { AvatarHasName } from '../../../../components/Common/Avatar'
import SelectPeoplePopup from '../../../../components/SelectPeoplePopup/SelectPeoplePopup'
import SpinnerInlineComponent from '../../../../components/SpinnerInlineComponent'
import { ASSIGNEE_TYPE } from '../../../../constants'
import validator from '../../../../validator'
import {
  FileAction,
  FileItem,
  SignDocumentList,
} from '../../WorkDocumentSignDocDetailPage/workDocumentSignDocDetailPageStyled'
import {
  CustomStepIcon,
  ModalCreateSignDocWrapper,
  StepsWrapper,
  UploadFileListItem,
} from './PopupCreateSignDocStyled'

const { Step } = Steps

const UPLOAD_BATCH_SIZE = 3

const dedupeFileList = fileList =>
  fileList.filter(
    (file, index, arr) => arr.findIndex(f => f.uid === file.uid) === index
  )

const uploadFilesInBatches = (fileStore, files, batchSize = UPLOAD_BATCH_SIZE) => {
  const uploadBatch = async startIndex => {
    const batch = files.slice(startIndex, startIndex + batchSize)
    if (batch.length === 0) return []

    const results = await Promise.all(
      batch.map(file => {
        const formData = new FormData()
        formData.append('file', file)
        return fileStore.uploadFile(formData)
      })
    )

    const nextResults = await uploadBatch(startIndex + batchSize)
    return [...results, ...nextResults]
  }

  return uploadBatch(0)
}

const beforeUploadPdf = file => {
  const isPdf =
    file.type === 'application/pdf' ||
    file.name?.toLowerCase().endsWith('.pdf')
  if (!isPdf) {
    message.error(`${file.name} không phải file PDF!`)
    return Upload.LIST_IGNORE
  }
  return false
}

const PopupCreateSignDoc = props => {
  const {
    loadingAnimationStore,
    electronicDocumentStore,
    fileStore,
    isVisiblePopupCreateSignDoc,
    handleClosePopupCreateSignDoc,
    selectPeopleStore,
    authenticationStore,
  } = props

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
  } = selectPeopleStore

  const [form] = Form.useForm()
  const [fileList, setFileList] = useState([])
  const [annexAttachedFiles, setAnnexAttachedFiles] = useState([])
  const [electronicDocument, setElectronicDocument] = useState([])

  const { currentUser } = authenticationStore

  const [KTATList, setKTATList] = useState([])
  const [leadKTATList, setLeadKTATList] = useState([])
  const [BODList, setBODlist] = useState([])
  const [formApproverList, setFormApproverList] = useState([])
  const [leaderApproverList, setLeaderApproverList] = useState([])
  const [digitalSignerList, setDigitalSignerList] = useState([])
  const [releaserList, setReleaserList] = useState([])
  const [permission, setPermission] = useState([
    ASSIGNEE_TYPE.LEADER_APPROVER,
    ASSIGNEE_TYPE.KTAT_OFFICE,
    ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
    ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
    ASSIGNEE_TYPE.FORM_APPROVER,
    ASSIGNEE_TYPE.DIGITAL_SIGNER,
    ASSIGNEE_TYPE.RELEASER,
  ])
  const [stepsData, setStepsData] = useState([])

  useEffect(() => {
    const allSteps = [
      {
        key: 1,
        title: 'Tạo dự thảo',
        userList: currentUser
          ? [
              {
                image_id: currentUser.imageId,
                full_name: currentUser.name_lowercase,
              },
            ]
          : [],
        allowDeleted: false,
      },
      {
        key: 2,
        title: 'Trưởng đơn vị ký',
        userList: leaderApproverList || [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.LEADER_APPROVER,
        permission: ASSIGNEE_TYPE.LEADER_APPROVER,
      },
      {
        key: 3,
        title: 'P.KTAT duyệt thể thức',
        userList: KTATList || [],
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.KTAT_OFFICE,
        permission: ASSIGNEE_TYPE.KTAT_OFFICE,
      },
      {
        key: 4,
        title: 'TP.KTAT ký',
        userList: leadKTATList || [],
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
        permission: ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
      },
      {
        key: 5,
        title: 'Giám đốc ký',
        userList: BODList || [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
        permission: ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
      },
      {
        key: 6,
        title: 'Đóng dấu',
        userList: digitalSignerList || [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.DIGITAL_SIGNER,
        permission: ASSIGNEE_TYPE.DIGITAL_SIGNER,
      },
      {
        key: 7,
        title: 'Phát hành',
        userList: releaserList || [],
        type: 'approval',
        allowDeleted: false,
        assignee_type: ASSIGNEE_TYPE.RELEASER,
        permission: ASSIGNEE_TYPE.RELEASER,
      },
    ]

    const filteredSteps = allSteps.filter(
      step => !step.permission || permission.includes(step.permission)
    )

    setStepsData(filteredSteps)
  }, [
    releaserList,
    currentUser,
    KTATList,
    leadKTATList,
    formApproverList,
    leaderApproverList,
    digitalSignerList,
    permission,
  ])

  useEffect(() => {
    ;(async () => {
      await selectPeopleStore.clearSelected()
    })()
  }, [])

  useEffect(() => {
    setKTATList([...(selectUserData[ASSIGNEE_TYPE.KTAT_OFFICE] || [])])
    setLeadKTATList([...(selectUserData[ASSIGNEE_TYPE.LEAD_KTAT_OFFICE] || [])])
    setBODlist([...(selectUserData[ASSIGNEE_TYPE.BOARD_OF_DIRECTOR] || [])])
    setFormApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.FORM_APPROVER] || []),
    ])
    setLeaderApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.LEADER_APPROVER] || []),
    ])

    setDigitalSignerList([
      ...(selectUserData[ASSIGNEE_TYPE.DIGITAL_SIGNER] || []),
    ])
    setReleaserList([...(selectUserData[ASSIGNEE_TYPE.RELEASER] || [])])
  }, [selectUserData, selectDepartmentData, selectGroupData])

  const handleChangeElectronicDoc = ({ fileList }) => {
    const nextFileList = dedupeFileList(fileList)
    setElectronicDocument(nextFileList)
    form.setFieldsValue({
      electronic_document: nextFileList.length ? nextFileList : undefined,
    })
  }

  const handleChangeFile = ({ fileList }) => {
    const nextFileList = dedupeFileList(fileList)
    setFileList(nextFileList)
  }

  const handleChangeAnnexFile = ({ fileList }) => {
    const nextFileList = dedupeFileList(fileList)
    setAnnexAttachedFiles(nextFileList)
  }

  const handleRemoveElectronicDoc = useCallback(
    uid => {
      const nextList = electronicDocument.filter(el => el.uid !== uid)
      setElectronicDocument(nextList)
      form.setFieldsValue({
        electronic_document: nextList.length ? nextList : undefined,
      })
    },
    [electronicDocument, form]
  )

  const handleRemoveAttachment = useCallback(
    uid => {
      setFileList(prev => prev.filter(el => el.uid !== uid))
    },
    []
  )

  const onFinish = async values => {
    if (!electronicDocument.length) {
      message.error('Vui lòng chọn tài liệu trình ký!')
      return
    }

    const uploadFileList = fileList
      .map(file => file.originFileObj)
      .filter(Boolean)

    const uploadAnnexFileList = annexAttachedFiles
      .map(file => file.originFileObj)
      .filter(Boolean)

    const uploadSignDocList = electronicDocument
      .map(file => file.originFileObj)
      .filter(Boolean)

    const assigness = stepsData
      .filter(el => el.key !== 1)
      .map(el => ({
        assignee_code: el.userList[0] ? el.userList[0].id : '',
        assignee_type: el.userList[0] ? el.userList[0].assignee_type : 'USER',
        permission: el.permission,
      }))

    const permission = stepsData
      .filter(el => el.key !== 1)
      .map(el => el.permission)
      .join(',')

    try {
      loadingAnimationStore.showSpinner(true)

      const [responseSignDocs, responseAttachments, responseAnnexAttachments] =
        await Promise.all([
          uploadFilesInBatches(fileStore, uploadSignDocList),
          uploadFilesInBatches(fileStore, uploadFileList),
          uploadFilesInBatches(fileStore, uploadAnnexFileList),
        ])

      const fileIdString = responseSignDocs
        .map(response => response.data.file_id)
        .join(',')

      const submitData = {
        title: values.title,
        attachments: responseAttachments.map(response => response.data.file_id),
        annex_attachments: responseAnnexAttachments.map(
          response => response.data.file_id
        ),
        file_id: fileIdString,
        assignees: assigness,
        permission: permission,
        type: 'WORK',
      }

      await electronicDocumentStore.createElectronicDocument(submitData)
      form.resetFields()
      setElectronicDocument([])
      setFileList([])
      setAnnexAttachedFiles([])
      selectPeopleStore.clearSelected()
      electronicDocumentStore.clearFilter()
      await electronicDocumentStore.getElectronicDocumentList('WORK')
      handleClosePopupCreateSignDoc()
      message.success('Tạo trình ký thành công!')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const handleRemoveStep = useCallback(
    key => {
      const newStepsData = stepsData.filter(step => step.key !== key)
      setStepsData(newStepsData)
      setPermission(
        newStepsData.filter(el => el.key !== 1).map(el => el.permission)
      )
    },
    [stepsData]
  )

  const existingStepKeys = useCallback(() => {
    return stepsData.map(step => step.key)
  }, [stepsData])

  const allSteps = [
    {
      key: 1,
      title: 'Tạo dự thảo',
      userList: currentUser
        ? [
            {
              image_id: currentUser.imageId,
              full_name: currentUser.name_lowercase,
            },
          ]
        : [],
      allowDeleted: false,
    },
    {
      key: 2,
      title: 'Trưởng đơn vị ký',
      userList: leaderApproverList ? leaderApproverList : [],
      type: 'approval',
      allowDeleted: true,
      assignee_type: ASSIGNEE_TYPE.LEADER_APPROVER,
      permission: ASSIGNEE_TYPE.LEADER_APPROVER,
    },
    {
      key: 3,
      title: 'P.KTAT duyệt thể thức',
      userList: KTATList ? KTATList : [],
      allowDeleted: true,
      assignee_type: ASSIGNEE_TYPE.KTAT_OFFICE,
      permission: ASSIGNEE_TYPE.KTAT_OFFICE,
    },
    {
      key: 4,
      title: 'TP.KTAT ký',
      userList: leadKTATList ? leadKTATList : [],
      type: 'approval',
      allowDeleted: true,
      assignee_type: ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
      permission: ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
    },
    {
      key: 5,
      title: 'Giám đốc ký',
      userList: BODList ? BODList : [],
      type: 'approval',
      allowDeleted: true,
      assignee_type: ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
      permission: ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
    },
    {
      key: 6,
      title: 'Đóng dấu',
      userList: digitalSignerList ? digitalSignerList : [],
      type: 'approval',
      allowDeleted: true,
      assignee_type: ASSIGNEE_TYPE.DIGITAL_SIGNER,
      permission: ASSIGNEE_TYPE.DIGITAL_SIGNER,
    },
    {
      key: 7,
      title: 'Phát hành',
      userList: releaserList ? releaserList : [],
      type: 'approval',
      allowDeleted: false,
      assignee_type: ASSIGNEE_TYPE.RELEASER,
      permission: ASSIGNEE_TYPE.RELEASER,
    },
  ]

  const handleAddStep = useCallback(
    key => {
      if (!key) return
      const stepToAdd = allSteps.find(step => step.key === key)
      if (stepToAdd) {
        const updatedSteps = prev => {
          const newSteps = [...prev, stepToAdd]
          newSteps.sort((a, b) => a.key - b.key)
          setPermission(
            newSteps.filter(el => el.key !== 1).map(el => el.permission)
          )
          return newSteps
        }

        setStepsData(updatedSteps)
      }
    },
    [allSteps]
  )

  const renderTitleStep = level => {
    return (
      <>
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>{level.title}</span>
            {level.key !== 1 && (
              <Tooltip title="Chọn người dùng" color={blue}>
                <Button
                  style={{ marginLeft: 8 }}
                  icon={<UserOutlined style={{ color: blue }} />}
                  onClick={() =>
                    handleOpenSelectUserDepartmentPopup(level.assignee_type)
                  }
                />
              </Tooltip>
            )}
          </div>
        </div>
      </>
    )
  }

  const renderDescStep = level => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: level.allow_edit ? 6 : 14,
          marginBottom: 14,
        }}>
        {level.userList[0] ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag
              className={'tag-selected'}
              color={'blue'}
              closable={level.key === 1 ? false : true}
              onClose={() =>
                selectPeopleStore.handleRemoveSelect(level.userList[0])
              }>
              <AvatarHasName
                imgId={level.userList[0]?.image_id}
                size={22}
                name={level.userList[0]?.full_name}
              />
            </Tag>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag style={{ borderRadius: 10 }}>Chưa gán người!</Tag>
          </div>
        )}
      </div>
    )
  }

  const handleClose = useCallback(() => {
    form.resetFields()
    setElectronicDocument([])
    setFileList([])
    setAnnexAttachedFiles([])
    selectPeopleStore.clearSelected()
    handleClosePopupCreateSignDoc()
  }, [
    form,
    electronicDocument,
    fileList,
    annexAttachedFiles,
    selectPeopleStore,
  ])

  return (
    <>
      <ModalCreateSignDocWrapper
        visible={isVisiblePopupCreateSignDoc}
        footer={null}
        closable={false}
        title={'Tạo mới'}
        width={1000}
        style={{ top: 60 }}>
        <SpinnerInlineComponent />
        <Form onFinish={onFinish} layout={'vertical'} name={'form'} form={form}>
          <Form.Item
            label={'Tiêu đề:'}
            name={'title'}
            rules={[
              { required: true, message: ' Vui lòng nhập tiêu đề trình ký!' },
              { validator: validator.validateInputString },
              { max: 200, message: 'Tiêu đề không được dài quá 200 kí tự' },
            ]}>
            <Input placeholder={'Nhập tiêu đề...'} />
          </Form.Item>
          <Form.Item
            label={'Tài liệu trình ký:'}
            name={'electronic_document'}
            rules={[
              {
                validator: () =>
                  electronicDocument.length
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(' Vui lòng chọn tài liệu trình ký!')
                      ),
              },
            ]}>
            <Upload
              fileList={electronicDocument}
              multiple
              beforeUpload={beforeUploadPdf}
              onChange={handleChangeElectronicDoc}
              showUploadList={false}
              accept={'application/pdf,.pdf'}>
              <Button icon={<UploadOutlined />}>Chọn tài liệu trình ký</Button>
            </Upload>
          </Form.Item>
          <SignDocumentList $scrollable={electronicDocument.length > 5}>
            {electronicDocument.map((file, index) => {
              return (
                <UploadFileListItem
                  key={file.uid || file.id}
                  isSigned={file.isSigned}
                  isNotPDF={file.type !== 'application/pdf'}>
                  <FileTextOutlined />
                  <span>{file.name}</span>
                  <Tooltip title={'Xóa'}>
                    <Button
                      type={'text'}
                      icon={<DeleteOutlined style={{ color: redPrimary }} />}
                      onClick={() => handleRemoveElectronicDoc(file.uid)}
                    />
                  </Tooltip>
                </UploadFileListItem>
              )
            })}
          </SignDocumentList>
          <Form.Item label={'Tài liệu đính kèm:'} name={'file'}>
            <Upload
              fileList={fileList}
              multiple
              beforeUpload={() => false}
              onChange={handleChangeFile}
              showUploadList={false}>
              <Button icon={<UploadOutlined />}>Chọn tài liệu đính kèm</Button>
            </Upload>
          </Form.Item>
          <SignDocumentList $scrollable={fileList.length > 5}>
            {fileList.map((file, index) => {
              return (
                <FileItem key={file.uid}>
                  <span>
                    <FileTextOutlined style={{ marginRight: 5 }} />
                    {file.name}
                  </span>
                  <FileAction>
                    <Tooltip title={'Xóa'}>
                      <Button
                        type={'text'}
                        icon={<DeleteOutlined style={{ color: redPrimary }} />}
                        onClick={() => handleRemoveAttachment(file.uid)}
                      />
                    </Tooltip>
                  </FileAction>
                </FileItem>
              )
            })}
          </SignDocumentList>

          <StepsWrapper count={stepsData.length} i={4}>
            <Steps
              status="process"
              className={'steps'}
              labelPlacement={'vertical'}
              size={'small'}>
              {stepsData.slice(0, 4).map(level => {
                return (
                  <Step
                    key={level.level}
                    title={renderTitleStep(level)}
                    description={renderDescStep(level)}
                    icon={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CustomStepIcon>{level.key}</CustomStepIcon>
                      </div>
                    }
                  />
                )
              })}
            </Steps>

            {stepsData.length > 4 && (
              <Steps
                status="process"
                className={'steps'}
                labelPlacement={'vertical'}
                size={'small'}
                style={{ marginTop: 20 }}>
                {stepsData.slice(4).map(level => {
                  return (
                    <Step
                      key={level.level}
                      title={renderTitleStep(level)}
                      description={renderDescStep(level)}
                      icon={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <CustomStepIcon>{level.key}</CustomStepIcon>
                        </div>
                      }
                    />
                  )
                })}
              </Steps>
            )}
          </StepsWrapper>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 20,
            }}>
            <Space>
              <Button danger icon={<CloseOutlined />} onClick={handleClose}>
                Hủy tạo
              </Button>
              <Button
                htmlType={'submit'}
                icon={<PlusOutlined />}
                type={'primary'}>
                Tạo trình ký
              </Button>
            </Space>
          </div>
        </Form>

        <SelectPeoplePopup
          onlyOneUser={true}
          handleCancelSelectUser={handleCancelSelectUserDepartment}
          handleSubmitSelectUser={() =>
            setIsVisibleSelectUserDepartmentPopup(false)
          }
          isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
        />
      </ModalCreateSignDocWrapper>
    </>
  )
}

PopupCreateSignDoc.propTypes = {}

export default memo(
  inject(
    'loadingAnimationStore',
    'fileStore',
    'electronicDocumentStore',
    'selectPeopleStore',
    'authenticationStore'
  )(observer(PopupCreateSignDoc))
)
